import React, { useState, useRef, useEffect } from 'react';
import './index.css'; // Keep your existing CSS

const toUTCDate = (date) => {
  if (!date) {
    console.warn('toUTCDate: null date provided');
    return new Date();
  }
  try {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  } catch (error) {
    console.error('toUTCDate: Error converting date', error, date);
    return new Date();
  }
};


const DateRangeSlider = (props) => {
  const {
    calenderStartDate,
    calenderEndDate,
    cycleStartDate,
    cycleEndDate,
    initialStartDateForSlicer,
    initialEndDateForSlicer,
    useCalenderStartDate,
    useCalenderEndDate
  } = props;

  // Constants
  const minDate = initialStartDateForSlicer;
  const maxDate = initialEndDateForSlicer;
  const step = 24 * 60 * 60 * 1000; // One day in ms

  // Helper to calculate day difference with null checks
  const dayDiff = (d1, d2) => {
    // Wait for both dates to be available
    if (!d1 || !d2) {
      console.warn('dayDiff: Waiting for dates to be available', { d1, d2 });
      return 0;
    }
    
    try {
      const utc1 = Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate());
      const utc2 = Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate());
      return Math.floor((utc2 - utc1) / step);
    } catch (error) {
      console.error('dayDiff: Error calculating date difference', error, { d1, d2 });
      return 0;
    }
  };

  const totalDays = (minDate && maxDate) ? dayDiff(minDate, maxDate) : 0;

  const [start, setStart] = useState(0); // days from minDate
  const [end, setEnd] = useState(totalDays);
  const [calStartDate, setCalStartDate] = useState(0);
  const [calEndDate, setCalEndDate] = useState(totalDays);

  const rangeRef = useRef(null);

  const percent = (day) => (day / totalDays) * 100;

  const handleMouseDown = (thumb) => (e) => {
    e.preventDefault();

    const moveHandler = (moveEvent) => {
      const slider = rangeRef.current;
      const rect = slider.getBoundingClientRect();
      const x = moveEvent.clientX - rect.left;
      const percentVal = Math.min(Math.max(x / rect.width, 0), 1);
      const newDayIndex = Math.round(percentVal * totalDays);

      // Safety check for minDate availability
      if (!minDate) {
        console.warn('minDate not available yet');
        return;
      }
      
      const newDate = toUTCDate(minDate);
      newDate.setUTCDate(minDate.getUTCDate() + newDayIndex);


      if (thumb === 'start') {
        if (newDayIndex < end) {
          if (newDayIndex >= end) return;
          setStart(newDayIndex);
          calenderStartDate(newDate);
          setCalStartDate(newDate)
        }
      } else {
        if (newDayIndex > start) {
          if (newDayIndex <= start) return;
          setEnd(newDayIndex);
          calenderEndDate(newDate);
          setCalEndDate(newDate)
        }
      }
    };

    const upHandler = () => {
      document.removeEventListener('mousemove', moveHandler);
      document.removeEventListener('mouseup', upHandler);
    };

    document.addEventListener('mousemove', moveHandler);
    document.addEventListener('mouseup', upHandler);
  };

  useEffect(() => {
    // Wait for all required dates to be available
    if (!cycleStartDate || !cycleEndDate || !initialStartDateForSlicer || !initialEndDateForSlicer) {
      console.log('Waiting for dates to be available', {
        cycleStartDate: !!cycleStartDate,
        cycleEndDate: !!cycleEndDate,
        initialStartDateForSlicer: !!initialStartDateForSlicer,
        initialEndDateForSlicer: !!initialEndDateForSlicer
      });
      return;
    }

    try {
      /** The start date and end is calculation done using UTC */
      const startOffset = useCalenderStartDate && calStartDate? dayDiff(toUTCDate(initialStartDateForSlicer), toUTCDate(calStartDate)): dayDiff(toUTCDate(initialStartDateForSlicer), toUTCDate(cycleStartDate));
      const endOffset = useCalenderEndDate && calEndDate? dayDiff(toUTCDate(initialStartDateForSlicer), toUTCDate(calEndDate)): dayDiff(toUTCDate(initialStartDateForSlicer), toUTCDate(cycleEndDate));

      if (startOffset >= 0 && endOffset >= 0 && startOffset <= endOffset && endOffset <= totalDays) {
        setStart(startOffset);
        setEnd(endOffset);
      } else if( startOffset < 0){
        setStart(0);
        if(endOffset < totalDays) setEnd(totalDays);
        if(endOffset > totalDays) setEnd(totalDays);
      }else if (endOffset > totalDays) {
        setEnd(totalDays);
      } else if (endOffset < 0){
        setEnd(totalDays);
      }else {
        // Fallback to full range if invalid
        setStart(0);
        setEnd(endOffset);
      }
    } catch (error) {
      console.error('DateRangeSlider useEffect error:', error);
      // Fallback to safe defaults
      setStart(0);
      setEnd(totalDays || 0);
    }
  }, [useCalenderStartDate, useCalenderEndDate, cycleStartDate, cycleEndDate, initialStartDateForSlicer, initialEndDateForSlicer, calStartDate, calEndDate, totalDays]);

  return (
    <div className="date-range-container" style={{ marginTop: "6px", position: "absolute", marginLeft: "8px" }}>
      <div className="date-slider" ref={rangeRef}>
        <div className="date-track" />
        <div
          className="date-range"
          style={{
            left: `${percent(start)}%`,
            width: `${percent(end) - percent(start)}%`,
          }}
        />
        <div
          className="date-thumb date-start-thumb"
          onMouseDown={handleMouseDown('start')}
          style={{ left: `${percent(start)}%` }}
        />
        <div
          className="date-thumb date-end-thumb"
          onMouseDown={handleMouseDown('end')}
          style={{ left: `${percent(end)}%` }}
        />
      </div>
    </div>
  );
};

export default DateRangeSlider;
